// /apps/patient/src/pages/appointments/AppointmentDetail.tsx

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { patientApiService } from '../../services/api';
import { useOrgCalendarDisplay } from '../../hooks/useOrgCalendarDisplay';
import { PageHeader } from '../../components/layout/PageHeader';
import {
  Calendar, Clock, MapPin, Video, User, Phone, Mail,
  AlertCircle, FileText, CreditCard, Building2,
  CheckCircle, XCircle, Download,
  Share2, Bell, ChevronRight, Stethoscope
} from 'lucide-react';
import { format, formatDistanceToNow, isPast, isToday, isFuture, addHours, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { VideoJoinButton } from '../../components/video/VideoJoinButton';
import { ManageAppointmentInfo, readErrorMessage } from '../../components/appointments/manageTypes';
import { ManageWindowNotice } from '../../components/appointments/ManageWindowNotice';
import { CancelAppointmentDialog } from '../../components/appointments/CancelAppointmentDialog';

interface Appointment {
  id: string;
  created_at: string;
  updated_at: string;
  patient_id: string;
  provider_id: string;
  organization_id: string;
  location_id?: string;

  // Date/time fields
  appointment_date: string;
  start_time: string;
  end_time: string;
  timezone: string;
  start_timestamp: number;
  end_timestamp: number;
  duration_minutes: number;

  // Appointment details
  appointment_type: 'initial_evaluation' | 'follow_up' | 'treatment' | 'consultation' |
  'telehealth' | 'group_class' | 'wellness' | 'procedure' | 'other';
  visit_reason?: string;
  chief_complaint?: string;

  // Telehealth
  is_telehealth: boolean;
  video_platform?: string;
  video_room_url?: string;
  video_room_name?: string;
  video_passcode?: string;

  // Status
  status: 'scheduled' | 'confirmed' | 'checked_in' | 'in_room' | 'with_provider' |
  'completed' | 'cancelled' | 'no_show' | 'rescheduled';

  // Status timestamps
  confirmed_at?: string;
  checked_in_at?: string;
  cancelled_at?: string;
  completed_at?: string;
  rescheduled_at?: string;

  // Cancellation details
  cancellation_reason?: string;
  cancelled_by?: 'patient' | 'provider' | 'staff' | 'system';

  // Instructions and notes
  patient_instructions?: string;
  special_requirements?: string[];

  // Forms
  intake_forms_sent: boolean;
  intake_forms_completed: boolean;
  form_response_ids?: string[];

  // Insurance
  insurance_verified: boolean;
  copay_amount?: number;
  copay_collected: boolean;

  // Reminders
  reminder_sent: boolean;
  reminder_method?: 'email' | 'sms' | 'both' | 'none';

  // Related data
  encounter_id?: string;

  // Populated from joins
  provider?: {
    id: string;
    first_name: string;
    last_name: string;
    title?: string;
    email?: string;
    phone?: string;
    specialties?: string[];
  };
  location?: {
    id: string;
    name: string;
    address_line1?: string;
    address_line2?: string;
    city?: string;
    state?: string;
    zip?: string;
    phone?: string;
  };
}

export function AppointmentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const calendarDisplay = useOrgCalendarDisplay();
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [manageInfo, setManageInfo] = useState<ManageAppointmentInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (id) {
      fetchAppointment();
    }
  }, [id]);

  const fetchAppointment = async () => {
    try {
      setLoading(true);
      setError(null);
      const [response, manage] = await Promise.all([
        patientApiService.getAppointmentDetail(id!),
        // Manage info drives the cancel/reschedule buttons; when it fails they stay hidden.
        patientApiService.getAppointmentManageInfo(id!).catch(() => null)
      ]);
      setAppointment(response);
      setManageInfo(manage);
    } catch (err) {
      console.error('Error fetching appointment:', err);
      setError('Failed to load appointment details.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (reason: string) => {
    if (!appointment) return;

    try {
      setCancelling(true);
      setCancelError(null);
      await patientApiService.cancelAppointment(appointment.id, reason || null);

      // Refresh appointment data
      await fetchAppointment();
      setShowCancelModal(false);
    } catch (err) {
      console.error('Error cancelling appointment:', err);
      setCancelError(await readErrorMessage(err, 'Failed to cancel appointment. Please try again.'));
    } finally {
      setCancelling(false);
    }
  };

  const handleAddToCalendar = () => {
    if (!appointment) return;

    // Create calendar event details
    const startDate = new Date(appointment.start_timestamp);
    const endDate = new Date(appointment.end_timestamp);

    const eventDetails = {
      title: `Appointment: ${appointment.appointment_type.replace('_', ' ')}`,
      details: appointment.visit_reason || '',
      location: appointment.is_telehealth
        ? 'Telehealth Visit'
        : `${appointment.location?.name}, ${appointment.location?.address_line1}, ${appointment.location?.city}, ${appointment.location?.state}`,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    };

    // Google Calendar URL
    const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(eventDetails.title)}&dates=${startDate.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')}/${endDate.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')}&details=${encodeURIComponent(eventDetails.details)}&location=${encodeURIComponent(eventDetails.location)}`;

    window.open(googleUrl, '_blank');
  };

  const getStatusDisplay = () => {
    if (!appointment) return null;

    const statusConfig = {
      scheduled: { icon: Clock, color: 'gray', label: 'Scheduled' },
      confirmed: { icon: CheckCircle, color: 'green', label: 'Confirmed' },
      checked_in: { icon: CheckCircle, color: 'blue', label: 'Checked In' },
      completed: { icon: CheckCircle, color: 'blue', label: 'Completed' },
      cancelled: { icon: XCircle, color: 'red', label: 'Cancelled' },
      no_show: { icon: AlertCircle, color: 'orange', label: 'No Show' },
    };

    const config = statusConfig[appointment.status] || statusConfig.scheduled;
    const Icon = config.icon;

    return (
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border bg-${config.color}-50 text-${config.color}-700 border-${config.color}-200`}>
        <Icon className="h-4 w-4" />
        {config.label}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex-1 bg-gray-50">
        <PageHeader title="Appointment Details" showBack />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error || !appointment) {
    return (
      <div className="flex-1 bg-gray-50">
        <PageHeader title="Appointment Details" showBack />
        <div className="p-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error || 'Appointment not found'}</p>
          </div>
        </div>
      </div>
    );
  }

  const appointmentDate = new Date(appointment.start_timestamp);
  const isPastAppointment = isPast(appointmentDate);
  const isTodayAppointment = isToday(appointmentDate);
  const isFutureAppointment = isFuture(appointmentDate);

  return (
    <div className="flex-1 bg-gray-50">
      <PageHeader
        title="Appointment Details"
        showBack
        actions={
          <div className="flex items-center gap-2">
            {appointment?.is_telehealth && appointment?.status !== 'cancelled' && (
              <VideoJoinButton appointment={appointment} />
            )}
            <button
              onClick={handleAddToCalendar}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Add to Calendar"
            >
              <Download className="h-5 w-5" />
            </button>
          </div>
        }
      />

      {/* Main Content */}
      <div className="p-4 space-y-4">
        {/* Header Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {format(appointmentDate, 'EEEE, MMMM d, yyyy')}
              </h2>
              <p className="text-lg text-gray-600 mt-1">
                {formatInTimeZone(
                  appointmentDate,
                  appointment.timezone,
                  'h:mm a'
                )} - {formatInTimeZone(
                  new Date(appointment.end_timestamp),
                  appointment.timezone,
                  'h:mm a zzz'
                )}
              </p>
              {isFutureAppointment && (
                <p className="text-sm text-primary font-medium mt-2">
                  {formatDistanceToNow(appointmentDate, { addSuffix: true })}
                </p>
              )}
            </div>
            {getStatusDisplay()}
          </div>

          {/* Appointment Type */}
          <div className="flex items-center gap-2 text-gray-700">
            <Stethoscope className="h-5 w-5 text-gray-400" />
            <span className="font-medium">
              {appointment.appointment_type.split('_').map(word =>
                word.charAt(0).toUpperCase() + word.slice(1)
              ).join(' ')}
            </span>
          </div>

          {!calendarDisplay.hide_visit_reason && (appointment.visit_reason || appointment.chief_complaint) && (
            <p className="text-gray-600 mt-2 pl-7">
              Reason: {appointment.visit_reason || appointment.chief_complaint}
            </p>
          )}
        </div>

        {/* Provider Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Provider</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <User className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">
                  {appointment.provider?.title && `${appointment.provider.title} `}
                  {appointment.provider?.first_name} {appointment.provider?.last_name}
                </p>
                {appointment.provider?.specialties && appointment.provider.specialties.length > 0 && (
                  <p className="text-sm text-gray-600">
                    {appointment.provider.specialties.join(', ')}
                  </p>
                )}
              </div>
            </div>

            {appointment.provider?.phone && (
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <Phone className="h-5 w-5 text-gray-600" />
                </div>
                <a
                  href={`tel:${appointment.provider.phone}`}
                  className="text-primary hover:underline"
                >
                  {appointment.provider.phone}
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Location or Telehealth Information */}
        {appointment.is_telehealth ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Telehealth Visit</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Video className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Virtual Appointment</p>
                  <p className="text-sm text-gray-600">
                    Platform: {appointment.video_platform || 'Daily.co'}
                  </p>
                </div>
              </div>

              {isTodayAppointment && appointment.video_room_url && (
                <button
                  onClick={() => window.open(appointment.video_room_url, '_blank')}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                  <Video className="h-5 w-5" />
                  Join Video Call
                </button>
              )}

              {!isTodayAppointment && appointment.video_room_url && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    The video link will be available on the day of your appointment.
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : appointment.location && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Location</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <Building2 className="h-5 w-5 text-gray-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{appointment.location.name}</p>
                  <p className="text-sm text-gray-600 mt-1">
                    {appointment.location.address_line1}
                    {appointment.location.address_line2 && <><br />{appointment.location.address_line2}</>}
                    <br />
                    {appointment.location.city}, {appointment.location.state} {appointment.location.zip}
                  </p>
                </div>
              </div>

              {appointment.location.phone && (
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <Phone className="h-5 w-5 text-gray-600" />
                  </div>
                  <a
                    href={`tel:${appointment.location.phone}`}
                    className="text-primary hover:underline"
                  >
                    {appointment.location.phone}
                  </a>
                </div>
              )}

              <button
                onClick={() => {
                  const address = `${appointment.location?.address_line1}, ${appointment.location?.city}, ${appointment.location?.state} ${appointment.location?.zip}`;
                  window.open(`https://maps.google.com/?q=${encodeURIComponent(address)}`, '_blank');
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <MapPin className="h-4 w-4" />
                Get Directions
              </button>
            </div>
          </div>
        )}

        {/* Patient Instructions */}
        {!calendarDisplay.hide_patient_instructions && appointment.patient_instructions && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-3">Instructions</h3>
            <p className="text-gray-600 whitespace-pre-wrap">
              {appointment.patient_instructions}
            </p>
          </div>
        )}

        {/* Special Requirements */}
        {appointment.special_requirements && appointment.special_requirements.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-3">Special Requirements</h3>
            <ul className="space-y-2">
              {appointment.special_requirements.map((req, index) => (
                <li key={index} className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                  <span className="text-gray-600">{req}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Forms Status */}
        {appointment.intake_forms_sent && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-3">Intake Forms</h3>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-gray-400" />
                <span className="text-gray-600">
                  {appointment.intake_forms_completed ? 'Forms completed' : 'Forms pending'}
                </span>
              </div>
              {appointment.intake_forms_completed ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <button
                  onClick={() => navigate('/forms')}
                  className="text-primary hover:underline text-sm font-medium"
                >
                  Complete Forms
                </button>
              )}
            </div>
          </div>
        )}

        {/* Insurance and Payment */}
        {(appointment.insurance_verified || appointment.copay_amount) && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-3">Insurance & Payment</h3>
            <div className="space-y-3">
              {appointment.insurance_verified && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Insurance Verified</span>
                  <CheckCircle className="h-5 w-5 text-green-500" />
                </div>
              )}
              {appointment.copay_amount !== undefined && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Copay Amount</span>
                  <span className="font-medium text-gray-900">
                    ${appointment.copay_amount.toFixed(2)}
                  </span>
                </div>
              )}
              {appointment.copay_collected && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Copay Collected</span>
                  <CheckCircle className="h-5 w-5 text-green-500" />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Cancellation Information */}
        {appointment.status === 'cancelled' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-red-900">Appointment Cancelled</p>
                {appointment.cancelled_at && (
                  <p className="text-sm text-red-700 mt-1">
                    Cancelled on {format(parseISO(appointment.cancelled_at), 'MMM d, yyyy at h:mm a')}
                  </p>
                )}
                {appointment.cancellation_reason && (
                  <p className="text-sm text-red-700 mt-1">
                    Reason: {appointment.cancellation_reason}
                  </p>
                )}
                {appointment.cancelled_by && (
                  <p className="text-sm text-red-700">
                    Cancelled by: {appointment.cancelled_by}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {isFutureAppointment && appointment.status !== 'cancelled' && (
          <div className="space-y-3">
            {/* Video Join Button - Most prominent for telehealth */}
            {appointment.is_telehealth && (
              <VideoJoinButton appointment={appointment} className="w-full" />
            )}
            {manageInfo?.can_cancel && (
              <button
                onClick={() => { setCancelError(null); setShowCancelModal(true); }}
                className="w-full px-4 py-3 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors"
              >
                Cancel Appointment
              </button>
            )}

            {manageInfo?.can_reschedule && (
              <button
                onClick={() => navigate(`/appointments/reschedule/${appointment.id}`)}
                className="w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Reschedule Appointment
              </button>
            )}

            {manageInfo && (manageInfo.can_manage === false || (manageInfo.can_manage === undefined && (!manageInfo.can_cancel || !manageInfo.can_reschedule))) && (
              <ManageWindowNotice
                windowHours={manageInfo.cancellation_window_hours}
                phone={manageInfo.organization_phone}
              />
            )}
          </div>
        )}

        {/* Past Appointment Actions */}
        {isPastAppointment && appointment.encounter_id && (
          <button
            onClick={() => navigate(`/records/${appointment.encounter_id}`)}
            className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <span className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-gray-600" />
              <span className="text-gray-700">View Visit Summary</span>
            </span>
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </button>
        )}
      </div>

      <CancelAppointmentDialog
        open={showCancelModal}
        cancelling={cancelling}
        error={cancelError}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancel}
      />
    </div>
  );
}